/**
 * CipBlockHandoff — per `rcp-cip-block-handoff.html`.
 *
 * Shown when CIP fails online and the customer must visit a branch.
 *
 * A11y fix (mockup #9):
 *   - Emoji / hero icon is `aria-hidden="true"` + text carries the meaning.
 *   - `role="status"` on dynamic success-state callouts (not static text).
 */

import { Link } from 'react-router';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Calendar, Phone } from 'lucide-react';

export function CipBlockHandoff(): JSX.Element {
  const { identity } = useAuth();
  const reason = identity?.cipBlockReason ?? 'We need another look at your information';
  return (
    <section aria-labelledby="cip-heading">
      <div className="card" style={{ textAlign: 'center', padding: 40 }}>
        <AlertCircle aria-hidden="true" size={56} color="var(--warn-text)" />
        <h1 id="cip-heading" tabIndex={-1} style={{ marginTop: 16 }}>
          Let&apos;s finish this in person
        </h1>
        <p style={{ fontSize: 16, color: 'var(--ink-700)', maxWidth: 520, margin: '12px auto' }}>
          For your security, we need to verify one more thing face-to-face. It&apos;s quick —
          usually 10 minutes at any of our branches.
        </p>
        <p style={{ color: 'var(--ink-500)', fontSize: 13 }}>
          Reason: <em>{reason}</em>
        </p>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
          <Link to="/appointment/book" className="btn btn-primary">
            <Calendar aria-hidden="true" size={16} />
            Book an in-branch appointment
          </Link>
          <a href="tel:15733645202" className="btn btn-secondary">
            <Phone aria-hidden="true" size={16} />
            Call us: 573-364-5202
          </a>
        </div>
      </div>

      <div className="card">
        <h2>Bring these with you</h2>
        <ul style={{ paddingLeft: 20, margin: 0 }}>
          <li>A valid government-issued photo ID (driver&apos;s license or state ID).</li>
          <li>Your Social Security card, or a document showing your SSN.</li>
          <li>Proof of address (utility bill, lease, or similar) within the last 60 days.</li>
        </ul>
      </div>
    </section>
  );
}
